/**
 * After a queued mutation syncs, merge the server entity into React Query list caches
 * and drop optimistic temp-* rows so lists do not show duplicates.
 */
import { queryClient, QueryKeys } from '../../../config/query-client.js';
import { createOptimisticListHelpers } from '../../utils/optimistic-list-cache.js';
import { athletesRepository, trainersRepository, programsRepository, dietsRepository, daybooksRepository } from '../../db/repositories.js';
import { API_CONFIG } from '../../../config/api.config.js';

const isAthleteListQuery = (queryKey) => {
  if (!Array.isArray(queryKey)) return false;
  if (queryKey.length === 1) return true;
  if (['list', 'my-athletes', 'search'].includes(queryKey[1])) return true;
  if (typeof queryKey[1] === 'number') return true;
  return false;
};

const isTrainerListQuery = (queryKey) => {
  if (!Array.isArray(queryKey)) return false;
  if (queryKey.length === 1) return true;
  if (queryKey[1] === 'search') return true;
  if (typeof queryKey[1] === 'number') return true;
  return false;
};

const isProgramListQuery = (queryKey) => {
  if (!Array.isArray(queryKey) || queryKey[0] !== 'programs') return false;
  if (queryKey.length === 1) return true;
  if (queryKey[1] === 'my-programs') return true;
  if (typeof queryKey[1] === 'number') return true;
  return false;
};

const isDietListQuery = (queryKey) => {
  if (!Array.isArray(queryKey)) return false;
  if (queryKey.length === 1) return true;
  if (typeof queryKey[1] === 'number') return true;
  return false;
};

const isDaybookListQuery = (queryKey) => {
  if (!Array.isArray(queryKey)) return false;
  if (queryKey.length === 1) return true;
  if (typeof queryKey[1] === 'number') return true;
  return false;
};

const ENTITY_RECONCILE_CONFIG = {
  athletes: {
    queryKeyRoot: QueryKeys.ATHLETES,
    listKey: 'athletes',
    showEndpoint: API_CONFIG.ENDPOINTS.ATHLETE.SHOW,
    repository: athletesRepository,
    isListQuery: isAthleteListQuery,
    pageSizeStorageKey: 'athletes',
    pickEntity: (serverResult) =>
      serverResult?.athlete || serverResult?.data?.athlete || serverResult?.data || serverResult,
  },
  trainers: {
    queryKeyRoot: QueryKeys.TRAINERS,
    listKey: 'trainers',
    showEndpoint: API_CONFIG.ENDPOINTS.TRAINER.SHOW,
    repository: trainersRepository,
    isListQuery: isTrainerListQuery,
    pageSizeStorageKey: 'trainers',
    pickEntity: (serverResult) =>
      serverResult?.trainer || serverResult?.data?.trainer || serverResult?.data || serverResult,
  },
  programs: {
    queryKeyRoot: QueryKeys.PROGRAMS,
    listKey: 'programs',
    showEndpoint: API_CONFIG.ENDPOINTS.PROGRAM.SHOW,
    repository: programsRepository,
    isListQuery: isProgramListQuery,
    pageSizeStorageKey: 'programs',
    pickEntity: (serverResult) =>
      serverResult?.program || serverResult?.data?.program || serverResult?.data || serverResult,
  },
  diets: {
    queryKeyRoot: QueryKeys.DIETS,
    listKey: 'diets',
    showEndpoint: API_CONFIG.ENDPOINTS.DIET.SHOW,
    repository: dietsRepository,
    isListQuery: isDietListQuery,
    pageSizeStorageKey: 'diets',
    pickEntity: (serverResult) =>
      serverResult?.diet || serverResult?.data?.diet || serverResult?.data || serverResult,
  },
  daybooks: {
    queryKeyRoot: QueryKeys.DAYBOOKS,
    listKey: 'daybooks',
    showEndpoint: API_CONFIG.ENDPOINTS.DAYBOOK.SHOW,
    repository: daybooksRepository,
    isListQuery: isDaybookListQuery,
    pageSizeStorageKey: 'daybooks',
    pickEntity: (serverResult) =>
      serverResult?.daybook || serverResult?.data?.daybook || serverResult?.data || serverResult,
  },
};

/**
 * @param {string} entityType
 * @param {string} operationType
 * @param {object|null} serverResult
 */
export async function reconcileListCachesAfterSync(entityType, operationType, serverResult) {
  if (!serverResult || operationType !== 'create') {
    return;
  }

  const config = ENTITY_RECONCILE_CONFIG[entityType];
  if (!config) return;

  const entity = config.pickEntity(serverResult);
  const id = entity?._id ?? entity?.id;
  if (!id) return;

  const listCache = createOptimisticListHelpers({
    listKey: config.listKey,
    showEndpoint: config.showEndpoint,
    repository: config.repository,
    isListQuery: config.isListQuery,
    queryKeyRoot: config.queryKeyRoot,
    pageSizeStorageKey: config.pageSizeStorageKey,
  });

  const merged = { ...entity, isPending: false };
  await listCache.applyListUpdate(queryClient, (list) =>
    listCache.mergeItemIntoList(list, merged)
  );
}

const LIST_REFETCH_ROOTS = {
  athletes: QueryKeys.ATHLETES,
  trainers: QueryKeys.TRAINERS,
  programs: QueryKeys.PROGRAMS,
  diets: QueryKeys.DIETS,
  daybooks: QueryKeys.DAYBOOKS,
  exercises: QueryKeys.EXERCISES,
  meals: QueryKeys.MEALS,
};

/**
 * Pull fresh list data from the server after sync so UI matches DB
 * (optimistic rows are replaced by authoritative server pages).
 */
export async function refetchEntityListsAfterSync(entityType) {
  const root = LIST_REFETCH_ROOTS[entityType];
  if (!root) return;

  await queryClient.refetchQueries({
    queryKey: root,
    type: 'active',
  });
}
